import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT, waLink, SERVICES } from "@/data/site";
import { toast } from "sonner";
import { MessageCircle, Loader2, CheckCircle2 } from "lucide-react";

const inputCls =
  "w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3 focus-visible:ring-2 focus-visible:ring-turq focus-visible:border-transparent transition-all outline-none text-navy h-auto";

export default function BookingModal({ open, onOpenChange, mode = "appointment", presetTest = "" }) {
  const isHome = mode === "home";
  const [form, setForm] = useState({ name: "", phone: "", email: "", test: "", date: "", time: "", address: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setDone(false); setForm((f) => ({ ...f, test: presetTest || "" })); }
  }, [open, presetTest]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and phone number."); return; }
    setLoading(true);
    try {
      if (isHome) {
        await api.post("/home-collection", {
          name: form.name, phone: form.phone, email: form.email, address: form.address, test: form.test, date: form.date, notes: form.notes,
        });
      } else {
        await api.post("/appointments", {
          name: form.name, phone: form.phone, email: form.email, test: form.test, date: form.date, time: form.time, notes: form.notes,
        });
      }
      setDone(true);
      toast.success("Request received! We will call you shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const waMessage = isHome
    ? `Hello DOABA, I want to book HOME COLLECTION.%0AName: ${form.name}%0APhone: ${form.phone}%0AAddress: ${form.address}%0ATest: ${form.test}%0ADate: ${form.date}`
    : `Hello DOABA, I want to book an APPOINTMENT.%0AName: ${form.name}%0APhone: ${form.phone}%0ATest: ${form.test}%0ADate: ${form.date} ${form.time}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] rounded-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto no-scrollbar" data-testid="booking-modal">
        <div className="bg-navy px-6 py-5">
          <DialogHeader>
            <DialogTitle className="text-white text-xl font-heading">
              {isHome ? "Book Home Collection" : "Book Appointment"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-turq-light text-sm mt-1">Fast confirmation • Same day reports</p>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="booking-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-heading text-navy">Request Received!</h3>
            <p className="text-secondary mt-2 text-sm">Our team will call you on {form.phone} shortly to confirm.</p>
            <a href={`tel:${CONTACT.phoneRaw}`} className="block mt-5 text-turq-dark font-medium">{CONTACT.phoneDisplay}</a>
            <Button onClick={() => onOpenChange(false)} className="mt-4 rounded-full bg-navy hover:bg-navy-light w-full" data-testid="booking-close-btn">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <Label className="text-navy text-sm mb-1.5 block">Full Name *</Label>
              <Input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="booking-name-input" />
            </div>
            <div>
              <Label className="text-navy text-sm mb-1.5 block">Phone Number *</Label>
              <Input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="booking-phone-input" />
            </div>
            <div>
              <Label className="text-navy text-sm mb-1.5 block">Email <span className="text-muted font-normal">(for confirmation)</span></Label>
              <Input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="booking-email-input" />
            </div>
            <div>
              <Label className="text-navy text-sm mb-1.5 block">Test</Label>
              <select className={inputCls} value={form.test} onChange={set("test")} data-testid="booking-test-select">
                <option value="">Select a test (optional)</option>
                {SERVICES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            {isHome && (
              <div>
                <Label className="text-navy text-sm mb-1.5 block">Home Address</Label>
                <Textarea className={inputCls} value={form.address} onChange={set("address")} placeholder="House no, street, area, city" data-testid="booking-address-input" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-navy text-sm mb-1.5 block">Date</Label>
                <Input type="date" className={inputCls} value={form.date} onChange={set("date")} data-testid="booking-date-input" />
              </div>
              {!isHome && (
                <div>
                  <Label className="text-navy text-sm mb-1.5 block">Time</Label>
                  <Input type="time" className={inputCls} value={form.time} onChange={set("time")} data-testid="booking-time-input" />
                </div>
              )}
            </div>

            <Button type="submit" disabled={loading} className="w-full rounded-full bg-turq hover:bg-turq-dark text-white py-6 text-base font-medium" data-testid="booking-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Confirm Booking"}
            </Button>
            <a href={waLink(waMessage.replace(/%0A/g, "\n"))} target="_blank" rel="noreferrer"
               className="flex items-center justify-center gap-2 w-full rounded-full border-2 border-emerald-500 text-emerald-600 py-3 font-medium hover:bg-emerald-50 transition-colors" data-testid="booking-whatsapp-btn">
              <MessageCircle className="w-4 h-4" /> Book via WhatsApp
            </a>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
